import styled from "styled-components";
import { theme } from "../theme";

export const MessagesPreviewContainer = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  box-shadow: ${theme.shadows.light};
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.xl};
    margin-bottom: ${theme.spacing.sm};
    
    /* Touch-friendly spacing */
    min-height: 60px;
  }
`;

export const MessagesPreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.sm};
    flex-wrap: wrap;
    gap: ${theme.spacing.xs};
  }
`;

export const MessagesPreviewTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    flex: 1;
    min-width: 0;
  }
`;

export const ViewAllButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  background: transparent;
  color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  flex-shrink: 0;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.light};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
    font-size: 0.8rem;
    min-height: 36px;
    
    &:active {
      transform: scale(0.95);
    }
  }
`;

export const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
    max-height: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    
    /* Hide scrollbar on mobile */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export const MessagePreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  border: 1px solid transparent;
  
  &:hover {
    background: ${theme.colors.background.secondary};
    border-color: ${theme.colors.border};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.sm};
    gap: ${theme.spacing.xs};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.lg};
    min-height: 64px;
    gap: ${theme.spacing.sm};
    
    &:active {
      background: ${theme.colors.background.secondary};
      transform: scale(0.98);
      border-color: ${theme.colors.primary};
    }
  }
`;

export const MessagePreviewAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.light};
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
`;

export const MessagePreviewContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 2px;
  }
`;

export const MessagePreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.xs};
`;

export const MessagePreviewName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

export const MessagePreviewSender = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
  }
`;

export const MessagePreviewTime = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
  }
`;

export const MessagePreviewText = styled.p`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    line-height: 1.4;
    /* Allow text to wrap on mobile for better readability */
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

export const UnreadCountBadge = styled.div`
  background: ${theme.colors.primary};
  color: ${theme.colors.text.light};
  font-size: 0.625rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
    padding: 3px 7px;
    min-width: 20px;
    border-radius: 12px;
  }
`;

export const RecentMessagesContainer = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  box-shadow: ${theme.shadows.light};
`;

export const RecentMessagesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

export const RecentMessagesTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

export const RecentMessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

export const RecentMessageItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

export const RecentMessageAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.light};
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
`;

export const RecentMessageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RecentMessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.xs};
`;

export const RecentMessageName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

export const RecentMessageTime = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

export const RecentMessageText = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EmptyMessagesState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.text.secondary};
  text-align: center;
`;

export const EmptyMessagesIcon = styled.div`
  font-size: 2rem;
  margin-bottom: ${theme.spacing.sm};
  opacity: 0.5;
`;

export const EmptyMessagesText = styled.p`
  font-size: 0.875rem;
  margin: 0;
`;

// Unread Messages Count Widget Styles
export const UnreadCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.lg};
    margin-bottom: ${theme.spacing.sm};
    min-height: 48px;
  }
`;

export const UnreadCountButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  width: 100%;
  
  &:hover {
    background: ${theme.colors.background.secondary};
    border-color: ${theme.colors.primary};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    gap: ${theme.spacing.xs};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.lg};
    min-height: 48px;
    
    &:active {
      transform: scale(0.98);
      background: ${theme.colors.background.secondary};
    }
  }
`;

export const UnreadCountText = styled.span`
  font-size: 0.875rem;
  color: ${theme.colors.text.primary};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

// Quick Message Widget Styles
export const QuickMessageContainer = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  box-shadow: ${theme.shadows.light};
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.xl};
    margin-bottom: ${theme.spacing.sm};
  }
`;

export const QuickMessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.sm};
    flex-wrap: wrap;
    gap: ${theme.spacing.xs};
  }
`;

export const QuickMessageTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    flex: 1;
    min-width: 0;
  }
`;

export const QuickMessageForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.md};
  }
`;

export const QuickMessageRecipients = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
    justify-content: center;
  }
`;

export const QuickMessageRecipient = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${props => props.$isSelected ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.$isSelected ? 'rgba(102, 126, 234, 0.1)' : theme.colors.background.secondary};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: rgba(102, 126, 234, 0.1);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.lg};
    min-height: 44px;
    
    &:active {
      transform: scale(0.95);
    }
  }
`;

export const QuickMessageRecipientAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.light};
  font-weight: 600;
  font-size: 0.75rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

export const QuickMessageRecipientName = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

export const QuickMessageInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 80px;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.lg};
    font-size: 1rem; /* Prevent zoom on iOS */
    line-height: 1.5;
  }
`;

export const QuickMessageButton = styled.button<{ $disabled?: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.$disabled 
    ? theme.colors.text.secondary 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${theme.colors.text.light};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  font-weight: 600;
  transition: all ${theme.transitions.normal};
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:disabled {
    opacity: 0.6;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.lg};
    font-size: 1rem;
    min-height: 48px;
    
    &:active:not(:disabled) {
      transform: scale(0.98);
    }
  }
`;

export const QuickMessageError = styled.div`
  color: ${theme.colors.error};
  font-size: 0.75rem;
  text-align: center;
  padding: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    padding: ${theme.spacing.sm};
  }
`;

export const MessagesWidgetLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: ${theme.colors.text.secondary};
`;

export const MessagesWidgetError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: ${theme.colors.error};
  text-align: center;
  padding: ${theme.spacing.md};
`;
